import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-12">
        <div className="brutalist-card p-8 text-center max-w-md" data-testid="invalid-token-message">
          <h1 className="text-2xl font-bold mb-3">Invalid Reset Link</h1>
          <p className="text-sm text-[#4A4A4A] mb-6">This password reset link is invalid or missing. Please request a new one.</p>
          <Link to="/forgot-password" className="brutalist-btn-primary inline-block text-sm" data-testid="request-new-link">
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm font-semibold mb-6 hover:underline" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to Login
        </Link>

        <div className="brutalist-card p-8">
          {success ? (
            <div className="text-center" data-testid="reset-success-message">
              <div className="w-16 h-16 rounded-full bg-green-100 border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold mb-3">Password Reset!</h1>
              <p className="text-sm text-[#4A4A4A] mb-6">Your password has been updated. You can now log in with your new password.</p>
              <Link to="/login" className="brutalist-btn-primary inline-block text-sm" data-testid="go-to-login-btn">
                Go to Login
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2">Set New Password</h1>
              <p className="text-sm text-[#4A4A4A] mb-6">Enter your new password below.</p>

              {error && <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3 mb-4 text-sm text-red-700" data-testid="reset-error">{error}</div>}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider">New Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      required
                      className="brutalist-input mt-1 pr-10"
                      data-testid="new-password-input"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-[#4A4A4A]" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider">Confirm Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    className="brutalist-input mt-1"
                    data-testid="confirm-password-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="reset-submit-btn"
                >
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Resetting...</> : "Reset Password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
